

import React, { useState, useEffect, useCallback } from "react";
import {
  Modal,
  Box,
  Typography,
  Button,
  Grid,
  TextField,
  CircularProgress,
  MenuItem,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import { createAttendance, updateAttendance } from "../../../services/mentorServices/MentorServices";
import { toast } from "react-toastify";
import { getAllCoeLabs } from "../../../services/mentorServices/DailyPracticeServices";

const validateTimeDifference = (start, end) => {
  const startDate = new Date(`1970-01-01T${start}:00`);
  const endDate = new Date(`1970-01-01T${end}:00`);
  return endDate - startDate >= 30 * 60 * 1000; // Ensuring at least 30 minutes gap
};

const INITIAL_FORM_STATE = {
  COE: "",
  labName: "",
  sessionStartTime: "",
  sessionEndTime: "",
  isChecked: false,
  attendanceName: "",
  geo_fencing_allowed: false,
};

const AttendanceModal = ({ open, onClose, editRecord, fetchAllAttendanceRecords, allCOE }) => {
  const [formValues, setFormValues] = useState(INITIAL_FORM_STATE);
  const [formErrors, setFormErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [allLabs, setLabs] = useState([]);

  useEffect(() => {
    setFormValues(INITIAL_FORM_STATE);
    setFormErrors({});
  }, [open]);

  const convertTo24HourFormat = (time='12') => {
    if (time==null) return
  const [hour, minuteAndPeriod] = time?.split(':');
  const [minute, period] = minuteAndPeriod?.split(' ');

  let hour24 = parseInt(hour);
  if (period === 'PM' && hour24 !== 12) hour24 += 12;
  if (period === 'AM' && hour24 === 12) hour24 = 0;
  
  return `${hour24.toString().padStart(2, '0')}:${minute}`;
};
const convertTo12HourFormat = (time = '12') => {
    let [hour, minute] = time?.split(':');
    hour = parseInt(hour);
    const period = hour >= 12 ? 'PM' : 'AM';
    const newHour = hour % 12 || 12;
    return `${newHour.toString().padStart(2, '0')}:${minute.padStart(2, '0')} ${period}`;
  };
  useEffect(() => {
    if (editRecord) {
      setFormValues({
        COE: editRecord?.coe_id || "",
        labName: editRecord?.lab?.id || "",
        isChecked: editRecord?.isCheckoutEnabled || false,
        sessionStartTime: convertTo24HourFormat(editRecord?.sessionStartTime) || "",
        sessionEndTime: convertTo24HourFormat(editRecord?.sessionEndTime) || "",
        attendanceName: editRecord?.attendanceName || "",
        geo_fencing_allowed: editRecord.geo_fencing_allowed?true: false,
      });
    }
  }, [editRecord, allCOE]);

  useEffect(() => {
    if (formValues?.COE) fetchLabs(formValues?.COE);
  }, [formValues?.COE]);

  const fetchLabs = useCallback(async (coeId) => {
    try {
      const response = await getAllCoeLabs(coeId);
      setLabs(response?.data.data || []);
    } catch (error) {
      setLabs([]);
      toast.error(error?.response?.data.message || error?.message || error?.error);
    }
  }, []);

  const handleInputChange = ({ target: { name, value, checked, type } }) => {
    setFormValues((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const validateFields = () => {
    const errors = {};
    const { COE, labName, sessionStartTime, sessionEndTime, attendanceName } = formValues;

    if (!COE) errors.COE = "COE is required*";
    if (!labName) errors.labName = "Lab Name is required*";
    if (!sessionStartTime) errors.sessionStartTime = "Start Time is required*";
    if (!sessionEndTime) errors.sessionEndTime = "End Time is required*";
    if (!attendanceName) errors.attendanceName = "Attendance Name is required*";
    if (sessionStartTime && sessionEndTime && !validateTimeDifference(sessionStartTime, sessionEndTime)) {
      errors.sessionEndTime = "End Time must be at least 30 minutes after Start Time (using 24Hr format)*.";
    }

    return errors;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const errors = validateFields();
    if (Object.keys(errors).length) {
      setFormErrors(errors);
      return;
    }

    setIsLoading(true);

    const requestAttendance = {
      coe_id: formValues.COE,
      lab_id: formValues.labName,
      isCheckoutEnabled: formValues.isChecked,
      sessionStartTime: convertTo12HourFormat(formValues.sessionStartTime),
      sessionEndTime: convertTo12HourFormat(formValues.sessionEndTime),
      attendanceName: formValues.attendanceName,
      geo_fencing_allowed: formValues.geo_fencing_allowed,
    };

    try {
      if (editRecord) {
        const response = await updateAttendance(editRecord.dailyAttendanceId, requestAttendance);
        toast.success(response?.data.message);
      } else {
        const response = await createAttendance(requestAttendance);
        toast.success(response?.data.message);
      }
      setFormValues(INITIAL_FORM_STATE);
      fetchAllAttendanceRecords();
      onClose();
    } catch (error) {
      toast.error(error?.response?.data.message || error?.message || error?.error);
    } finally {
      setIsLoading(false);
    }
  };
  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "45%",
          bgcolor: "background.paper",
          boxShadow: 24,
          p: 4,
          borderRadius: 2,
        }}
      >
        <Typography variant="h6" sx={{ mb: 2 }}>
          {editRecord ? "Edit Attendance" : "Create Attendance"}
        </Typography>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            {/* COE Dropdown */}
            <Grid item xs={12}>
              <TextField fullWidth select label="COE *" name="COE" value={formValues.COE} onChange={handleInputChange} error={!!formErrors.COE} helperText={formErrors.COE} size="small">
                {allCOE.map(({ id, name }) => (
                  <MenuItem key={id} value={id}>{name}</MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* Lab Name Dropdown */}
            <Grid item xs={12}>
              <TextField fullWidth select label="Lab Name *" name="labName" value={formValues.labName} onChange={handleInputChange} error={!!formErrors.labName} helperText={formErrors.labName} size="small">
                {allLabs.map(({ id, lab_name }) => (
                  <MenuItem key={id} value={id}>{lab_name}</MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* Attendance Name */}
            <Grid item xs={12}>
              <TextField fullWidth size="small"  name="attendanceName" value={formValues.attendanceName} onChange={handleInputChange} error={!!formErrors.attendanceName} helperText={formErrors.attendanceName} label="Attendance Name *" />
            </Grid>

            {/* Session Start Time */}
            <Grid item xs={12}>
              <TextField fullWidth type="time" InputLabelProps={{shrink: true}} name="sessionStartTime" value={formValues.sessionStartTime} onChange={handleInputChange} error={!!formErrors.sessionStartTime} helperText={formErrors.sessionStartTime} label="Session Start Time *" size="small" />
            </Grid>

            {/* Session End Time */}
            <Grid item xs={12}>
              <TextField fullWidth type="time" InputLabelProps={{shrink: true}} name="sessionEndTime" value={formValues.sessionEndTime} onChange={handleInputChange} error={!!formErrors.sessionEndTime} helperText={formErrors.sessionEndTime} label="Session End Time *" size="small" />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel control={<Checkbox checked={formValues.isChecked} onChange={handleInputChange} name="isChecked" />} label="Checkout" />
              <FormControlLabel control={<Checkbox checked={formValues.geo_fencing_allowed} onChange={handleInputChange} name="geo_fencing_allowed" />} label="Geo Fencing Allowed" />
            </Grid>
            {/* Submit Button */}
            <Grid item xs={12} sx={{ textAlign: "center" }}>
              <Button type="submit" variant="contained" color="primary" disabled={isLoading}>
                {isLoading ? <CircularProgress size={24} /> : editRecord ? "Update" : "Create"}
              </Button>
            </Grid>
          </Grid>
        </form>
      </Box>
    </Modal>
  );
};

export default AttendanceModal;

   